import RichTextEditor from 'components/editor/RichTextEditor';
import { RichTextEditorProps } from 'components/editor/RichTextEditor.types';
import { useFormContext } from 'react-hook-form';
import GenericFormField from '../genericFormField/GenericFormField';
import {
  FieldRendererProps,
  GenericFormFieldBaseProps,
} from '../genericFormField/GenericFormField.types';
import { RichTextEditorFieldStyled } from './RichTextEditorField.styles';

export interface RichTextEditorFieldProps
  extends Omit<GenericFormFieldBaseProps, 'fieldRenderer' | 'name'> {
  controlsSettings?: RichTextEditorProps['controlsSettings'];
  customFooter?: RichTextEditorProps['customFooter'];
  linkMode?: RichTextEditorProps['linkMode'];
  name: string;
  placeholderText?: string;
  readOnly?: boolean;
  toolbarSettings?: RichTextEditorProps['toolbarSettings'];
  /**
   * If you want to strip the HTML tags from the editor, set this to `true`. This is useful if you want to store the content as plain text.
   *
   * The default is `false`. The value is stored in `{name}_htmlContent` field. The value contains line breaks (\n).
   */
  stripHTML?: RichTextEditorProps['stripHTML'];
  useInsertParagraphPlugin?: RichTextEditorProps['useInsertParagraphPlugin'];
}

const RichTextEditorField = ({
  controlsSettings,
  customFooter,
  linkMode,
  name,
  placeholderText,
  readOnly,
  toolbarSettings,
  stripHTML,
  useInsertParagraphPlugin,
  ...propsGenericField
}: RichTextEditorFieldProps) => {
  const { trigger, setValue } = useFormContext();
  const defaultControlsSettings = {
    attachmentsAvailable: false,
    attachmentIconDisabled: false,
    isLoading: false,
    clearEditorButton: false,
  };

  const fieldRenderer = ({ field: { value } }: FieldRendererProps) => {
    const onBlur = () => {
      // Triggers the form validation when the field loses focus
      trigger();
    };

    const onChange = (htmlContent: string) => {
      // If we use the same name, the field value is never updated and the editor adds a new line for every keystroke
      setValue(`${name}_htmlContent`, htmlContent, {
        shouldDirty: true,
      });
    };

    return (
      <RichTextEditorFieldStyled>
        <RichTextEditor
          contents={value}
          controlsSettings={controlsSettings ?? defaultControlsSettings}
          customFooter={customFooter}
          id={name}
          linkMode={linkMode}
          placeholderText={placeholderText}
          onBlur={onBlur}
          onChange={onChange}
          readOnly={readOnly}
          stripHTML={stripHTML}
          toolbarSettings={toolbarSettings}
          useInsertParagraphPlugin={useInsertParagraphPlugin}
        />
      </RichTextEditorFieldStyled>
    );
  };

  return (
    <GenericFormField
      fieldRenderer={fieldRenderer}
      name={name}
      {...propsGenericField}
    />
  );
};

RichTextEditorField.defaultProps = {
  controlsSettings: undefined,
  customFooter: undefined,
  linkMode: undefined,
  placeholderText: undefined,
  readOnly: false,
  toolbarSettings: undefined,
  stripHTML: false,
  useInsertParagraphPlugin: undefined,
};

export default RichTextEditorField;
